import './CountryRow.css';
import { MiniProgressBar } from '../../components/MiniProgressBar';
import { Portrait } from '../../components/Portrait';
import { UfTile } from '../../components/UfTile';
import { t } from '../../copy/strings';
import { fmtPct, fmtSections, fmtVoters } from '../../format/numbers';
import { fmtHourShort } from '../../format/time';
import { type CountryRow as CountryData, leaderOf, rowStatus } from '../../model/abroad';
import type { Candidate } from '../../model/types';
import { openPlace } from '../../state/actions';
import { partyClass, partyOf } from '../../theme/parties';

export interface CountryRowProps {
  row: CountryData;
  pair: readonly [Candidate, Candidate] | null;
  photos: boolean;
  /** Linha do instante de replay: navegar continua valendo. */
  interactive?: boolean;
}

/**
 * Linha de país do cartão do Exterior (§8.5 CountryRow, 50 px): ladrilho com o ISO na cor do líder (ou "sem dados"),
 * nome e "{N} cidades · {eleitores}", à direita o retrato e o % do líder ou a situação ("apurado", "apurando", "urnas
 * fechadas", "vota até {hora}"), e a minibarra das seções. Abre o país.
 */
export function CountryRow({ row, pair, photos, interactive = true }: CountryRowProps) {
  const { country } = row;
  const lead = pair ? leaderOf(row.shares) : null;
  const leader = lead === null || !pair ? null : pair[lead];
  const status = rowStatus(row.sectionsFraction, row.closed);
  const sub =
    country.n_cities > 1
      ? t('country.row.sub', { N: country.n_cities, E: fmtVoters(country.electorate_ref) })
      : fmtVoters(country.electorate_ref);
  const S = fmtSections(row.sectionsFraction);
  const aria =
    leader && row.shares && lead !== null
      ? t('country.row.aria', { PAIS: country.name, X: leader.display_name, pX: fmtPct(row.shares[lead]), S })
      : status === 'closed'
        ? t('country.row.aria.closed', { PAIS: country.name, S })
        : t('country.row.aria.status', { PAIS: country.name, STATUS: statusText(status, row.closesTMin), S });
  return (
    <button
      type="button"
      class="country-row"
      data-testid="country-row"
      data-iso={country.iso2}
      aria-label={aria}
      disabled={!interactive}
      onClick={() => openPlace({ kind: 'country', iso2: country.iso2 })}
    >
      <UfTile
        code={country.iso2}
        fill={leader ? 'leader' : 'nodata'}
        party={leader ? partyOf(leader) : undefined}
        size="row"
      />
      <span class="country-row__text">
        <span class="country-row__name">{country.name}</span>
        <span class="country-row__sub">{sub}</span>
      </span>
      {leader && row.shares && lead !== null ? (
        <span class={`country-row__value ${partyClass(partyOf(leader))}`}>
          <Portrait candidate={leader} photo={photos ? leader.photo : null} size={22} />
          <span class="country-row__pct">{fmtPct(row.shares[lead])}</span>
        </span>
      ) : (
        <span class="country-row__status">{statusText(status, row.closesTMin)}</span>
      )}
      <span class="country-row__bar">
        <MiniProgressBar sectionsFraction={row.sectionsFraction} variant="thin" />
      </span>
    </button>
  );
}

/** O texto da situação de uma linha sem votos (§8.5). */
export function statusText(status: ReturnType<typeof rowStatus>, closesTMin: number | null): string {
  switch (status) {
    case 'counted':
      return t('row.status.counted');
    case 'counting':
      return t('row.status.counting');
    case 'closed':
      return t('row.status.closed');
    default:
      return closesTMin === null ? t('row.status.voting') : t('row.status.votesUntil', { h: fmtHourShort(closesTMin) });
  }
}
